import { useState, useEffect, useCallback, useMemo } from 'react';
import { parseCartAddToken } from './lib/cartToken';
import { parseContactHandoff } from './lib/contactHandoff';
import { fetchProductBySku, resolvePhotoUrl } from './lib/api';
import { getStoreBySiteId, STORES } from './lib/stores';
import {
  fetchCheckoutConfig,
  processCheckoutPayment,
  type CheckoutConfig,
} from './lib/checkout';
import {
  validateCoupon,
  type ValidateCouponResult,
  type ShippingEvaluateResult,
} from './lib/coupons';
import { useHashStep, type CheckoutStep } from './hooks/useHashStep';
import { useAvailabilityCheck } from './hooks/useAvailabilityCheck';
import { useCustomerAuth } from './hooks/useCustomerAuth';
import { customerContactFromProfile } from './lib/customerAuth';
import StepIndicator from './components/StepIndicator';
import CartStep from './components/CartStep';
import ShippingStep from './components/ShippingStep';
import PaymentStep from './components/PaymentStep';
import OrderConfirmation from './components/OrderConfirmation';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

function fireGa4Event(eventName: string, params?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

interface CartItem {
  sku: string;
  title: string;
  price: number;
  brand?: string;
  size?: string;
  photoUrl?: string;
  siteName: string;
  siteId: number;
}

interface CartState {
  items: CartItem[];
  createdAt: number;
  /** Stable session id for reservation ownership across retries/refreshes. */
  sessionId?: string;
  customerId?: number | null;
  customerContact?: { name: string; email: string; phone: string };
}

function ensureSessionId(state: CartState): CartState {
  if (state.sessionId) return state;
  const next = { ...state, sessionId: crypto.randomUUID() };
  saveCart(next);
  return next;
}

interface TaxLineDisplay {
  label: string;
  amount: number;
}

interface CompletedOrder {
  items: CartItem[];
  subtotal: number;
  taxLines: TaxLineDisplay[];
  shippingMethod: 'pickup' | 'canadapost' | 'ups';
  shippingCost: number;
  total: number;
  completedAt: number;
  orderNumber?: string;
  squareOrderId?: string;
  paymentId?: string;
  receiptToken?: string;
  customerName?: string;
  customerEmail?: string;
  inventoryMode?: 'live' | 'simulated';
  isTest?: boolean;
}

type ShippingMethod = 'pickup' | 'canadapost' | 'ups';

const CART_KEY = 'vc_cart';
const CART_EXPIRY_MS = 4 * 60 * 60 * 1000;
const VI_LOGO_URL = '/logos/vi-consign.png';

function loadCart(): CartState {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return { items: [], createdAt: Date.now() };
    const state: CartState = JSON.parse(raw);
    if (Date.now() - state.createdAt > CART_EXPIRY_MS) {
      localStorage.removeItem(CART_KEY);
      return { items: [], createdAt: Date.now() };
    }
    return state;
  } catch {
    return { items: [], createdAt: Date.now() };
  }
}

function saveCart(state: CartState) {
  localStorage.setItem(CART_KEY, JSON.stringify(state));
}

function getBannerLevel(sq: string, inv: string): string {
  if (sq === 'production' && inv === 'simulated') return 'danger';
  if (sq === 'sandbox' && inv === 'live') return 'warning';
  return 'info';
}

function getBannerMessage(sq: string, inv: string): string {
  if (sq === 'sandbox' && inv === 'simulated')
    return '\u{1F9EA} TEST MODE \u2014 No real charges, no inventory changes';
  if (sq === 'sandbox' && inv === 'live')
    return '\u26A0\uFE0F VERIFICATION MODE \u2014 Test cards only, inventory WILL be updated';
  if (sq === 'production' && inv === 'simulated')
    return '\u{1F6A8} DIAGNOSTIC MODE \u2014 Real charges enabled, inventory writes disabled';
  return '';
}

export default function App() {
  const { step, setStep, goBack } = useHashStep();
  const {
    customer,
    login,
    register,
    logout,
  } = useCustomerAuth();
  const [cart, setCart] = useState<CartState>({ items: [], createdAt: Date.now() });
  const [checkoutState, setCheckoutState] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [checkoutConfig, setCheckoutConfig] = useState<CheckoutConfig>({ enabled: false });
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod>('pickup');
  const [customerContact, setCustomerContact] = useState({ name: '', email: '', phone: '' });
  const [completedOrder, setCompletedOrder] = useState<CompletedOrder | null>(null);
  const [couponApplied, setCouponApplied] = useState<ValidateCouponResult | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [shippingEval, setShippingEval] = useState<ShippingEvaluateResult | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [lastAddedSiteId, setLastAddedSiteId] = useState<number | null>(null);

  const cartCheckoutItems = useMemo(
    () => cart.items.map((i) => ({ sku: i.sku, siteId: i.siteId })),
    [cart.items],
  );

  const {
    allAvailable,
    checking,
    getItemStatus,
    recheckNow,
  } = useAvailabilityCheck({
    items: cartCheckoutItems,
    cartSessionId: cart.sessionId || null,
    enabled: step !== 'payment' && cart.items.length > 0 && checkoutState !== 'success',
  });

  const addItem = useCallback((item: CartItem) => {
    setCart((prev) => {
      if (prev.items.filter((i) => i.sku === item.sku).length >= 10) return prev; // allow multiple but reasonable max per item limit
      if (prev.items.length >= 100) {
        console.warn('Cart item limit reached');
        return prev;
      }
      const next = { ...prev, items: [...prev.items, item] };
      saveCart(next);
      fireGa4Event('add_to_cart', {
        currency: 'CAD',
        value: item.price,
        items: [{ item_id: item.sku, item_name: item.title, price: item.price }],
      });
      return next;
    });
  }, []);

  const handleContactChange = useCallback(
    (contact: { name: string; email: string; phone: string }) => {
      setCustomerContact(contact);
      setCart((prev) => {
        const next = { ...prev, customerContact: contact };
        saveCart(next);
        return next;
      });
    },
    [],
  );

  // Autofill contact + customerId when auth state becomes available
  useEffect(() => {
    if (!customer) return;
    const contact = customerContactFromProfile(customer);
    setCustomerId(customer.id);
    setCustomerContact((prev) => ({
      name: prev.name.trim() || contact.name,
      email: prev.email.trim() || contact.email,
      phone: prev.phone.trim() || contact.phone,
    }));
    setCart((prev) => {
      const next = {
        ...prev,
        customerId: customer.id,
        customerContact: {
          name: (prev.customerContact?.name || '').trim() || contact.name,
          email: (prev.customerContact?.email || '').trim() || contact.email,
          phone: (prev.customerContact?.phone || '').trim() || contact.phone,
        },
      };
      saveCart(next);
      return next;
    });
  }, [customer]);

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      const result = await login(email, password);
      if (result.success && result.customer) {
        const contact = customerContactFromProfile(result.customer);
        setCustomerId(result.customer.id);
        handleContactChange(contact);
      }
      return result;
    },
    [login, handleContactChange],
  );

  const handleLogout = useCallback(() => {
    logout();
    setCustomerId(null);
  }, [logout]);

  const resolveAndAdd = useCallback(
    async (sku: string, siteId: number) => {
      setLoading(true);
      setAddError(null);
      try {
        const product = await fetchProductBySku(siteId, sku);
        if (!product) {
          setAddError(`Item ${sku} is no longer available.`);
          return;
        }
        if (!product.isAvailable) {
          setAddError(`${product.title || sku} has already been sold.`);
          return;
        }
        const store = getStoreBySiteId(siteId);
        addItem({
          sku: product.sku,
          title: product.title || product.sku,
          price: parseFloat(product.start_price),
          brand: product.brand || undefined,
          size: product.size || undefined,
          photoUrl: resolvePhotoUrl(product.primary_photo_url_thumb, product.primary_photo_url_full),
          siteName: store?.name || product.site_name,
          siteId,
        });
        setLastAddedSiteId(siteId);
      } catch {
        setAddError('Could not load item details. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [addItem],
  );

  useEffect(() => {
    const loaded = ensureSessionId(loadCart());
    setCart(loaded);
    if (loaded.customerId) setCustomerId(loaded.customerId);
    if (loaded.customerContact) setCustomerContact(loaded.customerContact);

    const params = new URLSearchParams(window.location.search);
    const addParam = params.get('add');
    const customerIdParam = params.get('customerId');
    const contactParam = params.get('contact');
    if (customerIdParam && /^\d+$/.test(customerIdParam)) {
      const id = Number(customerIdParam);
      setCustomerId(id);
      setCart((prev) => {
        const next = { ...prev, customerId: id };
        saveCart(next);
        return next;
      });
    }
    if (contactParam) {
      const handoff = parseContactHandoff(contactParam);
      if (handoff) {
        const contact = {
          name: handoff.name || '',
          email: handoff.email || '',
          phone: handoff.phone || '',
        };
        setCustomerContact(contact);
        setCart((prev) => {
          const next = { ...prev, customerContact: contact };
          saveCart(next);
          return next;
        });
      }
    }
    if (addParam || contactParam || customerIdParam) {
      // Strip handoff params from the URL after applying them
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
    if (addParam) {
      const token = parseCartAddToken(addParam);
      if (token) {
        resolveAndAdd(token.s, token.i);
      } else {
        setAddError('This add-to-cart link has expired or is invalid.');
      }
    }

    fetchCheckoutConfig()
      .then(setCheckoutConfig)
      .catch(() => setCheckoutConfig({ enabled: false }));
  }, [resolveAndAdd]);

  // Re-validate coupon when cart or shipping changes
  useEffect(() => {
    if (!couponApplied?.ok || !couponApplied.code) return;
    let cancelled = false;
    const couponLines = cart.items.map((item) => ({
      sku: item.sku,
      site_id: item.siteId,
      price_cents: Math.round(item.price * 100),
    }));
    (async () => {
      const result = await validateCoupon({
        code: couponApplied.code!,
        email: customerContact.email.trim(),
        customer_id: customerId,
        shipping_method: shippingMethod,
        shipping_cents: 0,
        lines: couponLines,
      });
      if (cancelled) return;
      if (!result.ok) {
        setCouponApplied(null);
        setCouponError(result.message || 'Coupon no longer applies to this cart.');
      } else {
        setCouponApplied(result);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.items.length, customerContact.email, shippingMethod]);

  // Abandoned cart signal
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        cart.items.length > 0 &&
        customerContact.email &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerContact.email) &&
        !completedOrder
      ) {
        const checkoutApiUrl =
          import.meta.env.VITE_CHECKOUT_API_URL ||
          import.meta.env.VITE_API_URL?.replace('/api/v1', '') ||
          'http://localhost:8787';
        navigator.sendBeacon(
          `${checkoutApiUrl}/abandon`,
          JSON.stringify({
            email: customerContact.email.trim(),
            name: customerContact.name.trim() || undefined,
            items: cart.items.map((i) => ({ sku: i.sku, siteId: i.siteId })),
          }),
        );
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cart.items, customerContact, completedOrder]);

  const removeItem = useCallback((sku: string) => {
    setCart((prev) => {
      const indexToRemove = prev.items.findIndex(i => i.sku === sku);
      if (indexToRemove === -1) return prev;
      const nextItems = [...prev.items];
      nextItems.splice(indexToRemove, 1);
      const next = { ...prev, items: nextItems };
      saveCart(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    const next = { items: [] as CartItem[], createdAt: Date.now() };
    saveCart(next);
    setCart(next);
    setCouponApplied(null);
    setCouponError(null);
    setStep('cart');
  }, [setStep]);

  const handleProceedToShipping = useCallback(async () => {
    const ok = await recheckNow();
    if (ok) {
      setStep('shipping');
      fireGa4Event('begin_checkout', {
        currency: 'CAD',
        value: cart.items.reduce((s, i) => s + i.price, 0),
        items: cart.items.map((i) => ({ item_id: i.sku, item_name: i.title, price: i.price })),
      });
    }
  }, [recheckNow, setStep, cart.items]);

  const handleProceedToPayment = useCallback(async () => {
    const ok = await recheckNow();
    if (ok) {
      setStep('payment');
      fireGa4Event('add_payment_info', {
        currency: 'CAD',
        value: cart.items.reduce((s, i) => s + i.price, 0),
        payment_type: 'card',
      });
    }
  }, [recheckNow, setStep, cart.items]);

  const handleCheckout = useCallback(
    async (sourceId: string, postalCode: string) => {
      if (!checkoutConfig.enabled) {
        setCheckoutError('Square checkout is not available right now.');
        setCheckoutState('error');
        return;
      }

      setCheckoutError(null);
      setCheckoutState('processing');

      const subtotal = cart.items.reduce((s, i) => s + i.price, 0);

      try {
        const result = await processCheckoutPayment({
          idempotencyKey: crypto.randomUUID(),
          sourceId,
          postalCode,
          items: cart.items.map((item) => ({ sku: item.sku, siteId: item.siteId })),
          customer: {
            name: customerContact.name.trim(),
            email: customerContact.email.trim(),
            phone: customerContact.phone.trim() || undefined,
            shippingMethod,
          },
          couponCode: couponApplied?.ok ? couponApplied.code : undefined,
          shippingCents: 0,
          customerId,
          cartSessionId: cart.sessionId || null,
        });

        if (result.success === false || (!result.success && !result.orderId)) {
          throw new Error('Payment was not completed');
        }

        const serverTotal = result.total ?? subtotal;

        // Build dynamic tax lines from server response
        const taxLines: TaxLineDisplay[] = [];
        if (result.hst && result.hst > 0) {
          taxLines.push({ label: `HST${result.taxProvince ? ` (${result.taxProvince})` : ''}`, amount: result.hst });
        } else {
          if (result.gst && result.gst > 0) {
            taxLines.push({ label: 'GST (5%)', amount: result.gst });
          }
          if (result.pst && result.pst > 0) {
            taxLines.push({ label: 'PST', amount: result.pst });
          }
        }

        fireGa4Event('purchase', {
          transaction_id: result.orderId || crypto.randomUUID(),
          currency: 'CAD',
          value: serverTotal,
          items: cart.items.map((i) => ({ item_id: i.sku, item_name: i.title, price: i.price })),
        });

        const serverSubtotal = result.subtotal ?? subtotal;
        const totalTax = taxLines.reduce((s, tl) => s + tl.amount, 0);

        setCompletedOrder({
          items: [...cart.items],
          subtotal,
          taxLines,
          shippingMethod,
          shippingCost: Math.max(0, serverTotal - serverSubtotal - totalTax),
          total: serverTotal,
          completedAt: Date.now(),
          orderNumber: result.orderNumber,
          squareOrderId: result.orderId,
          paymentId: result.paymentId,
          receiptToken: result.receiptToken,
          customerName: customerContact.name.trim(),
          customerEmail: customerContact.email.trim(),
          inventoryMode: result.inventoryMode === 'simulated' ? 'simulated' : 'live',
          isTest: result.isTest === true || result.squareMode === 'sandbox',
        });
        const nextCart = { items: [] as CartItem[], createdAt: Date.now() };
        saveCart(nextCart);
        setCart(nextCart);
        setCouponApplied(null);
        setCouponError(null);
        setCheckoutState('success');
      } catch (err) {
        fireGa4Event('payment_failed', {
          currency: 'CAD',
          value: subtotal,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
        setCheckoutError(err instanceof Error ? err.message : 'Payment failed');
        setCheckoutState('error');
      }
    },
    [cart.items, cart.sessionId, checkoutConfig, couponApplied, customerContact, shippingMethod, customerId],
  );

  const canNavigateToStep = useCallback(
    (target: CheckoutStep): boolean => {
      const stepOrder: CheckoutStep[] = ['cart', 'shipping', 'payment'];
      const currentIdx = stepOrder.indexOf(step);
      const targetIdx = stepOrder.indexOf(target);
      // Can always go back
      if (targetIdx <= currentIdx) return true;
      // Can't skip forward
      return false;
    },
    [step],
  );

  const handleStepClick = useCallback(
    (target: CheckoutStep) => {
      if (canNavigateToStep(target)) {
        setStep(target);
      }
    },
    [canNavigateToStep, setStep],
  );

  return (
    <div className="checkout-layout">
      <header className="checkout-header">
        <div className="checkout-header-brand">
          <img
            src={VI_LOGO_URL}
            alt="V.I. Consignment"
            className="checkout-header-logo"
            width={40}
            height={40}
          />
          <div>
            <h1>V.I. Consignment</h1>
            <p>Secure Checkout</p>
          </div>
        </div>
      </header>

      {(() => {
        const sq = checkoutConfig.squareMode || checkoutConfig.environment || 'sandbox';
        const inv = checkoutConfig.inventoryMode || 'simulated';
        if (sq === 'production' && inv === 'live') return null;
        const msg = getBannerMessage(sq, inv);
        if (!msg) return null;
        return (
          <div className={`mode-banner mode-banner--${getBannerLevel(sq, inv)}`}>
            {msg}
          </div>
        );
      })()}

      {checkoutState !== 'success' && cart.items.length > 0 && (
        <StepIndicator
          currentStep={step}
          onStepClick={handleStepClick}
          canNavigateTo={canNavigateToStep}
        />
      )}

      <main className="checkout-main">
        {loading && (
          <div className="status-banner info">Adding item to your cart...</div>
        )}

        {addError && (
          <div className="status-banner error">{addError}</div>
        )}

        {checkoutState === 'success' && completedOrder ? (
          <OrderConfirmation
            order={completedOrder}
            onContinueShopping={() => {
              setCompletedOrder(null);
              setCheckoutState('idle');
              setStep('cart');
            }}
          />
        ) : cart.items.length === 0 && !loading ? (
          <div className="card">
            <div className="empty-cart">
              <div className="empty-cart-icon">&#x1F6D2;</div>
              <p>Your cart is empty</p>
              <p style={{ fontSize: '0.85rem' }}>
                Browse our stores and add items to get started.
              </p>
              <div className="store-links">
                {STORES.map((store) => (
                  <a
                    key={store.siteId}
                    href={store.storefrontUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-link-btn"
                    title={store.name}
                  >
                    <img
                      src={store.logoUrl}
                      alt={store.name}
                      className="store-link-logo"
                    />
                    <span className="store-link-name">{store.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {step === 'cart' && (
              <CartStep
                items={cart.items}
                onRemoveItem={removeItem}
                onClearCart={clearCart}
                onProceed={handleProceedToShipping}
                couponApplied={couponApplied}
                onCouponApplied={setCouponApplied}
                couponError={couponError}
                onCouponError={setCouponError}
                customerEmail={customerContact.email}
                customerId={customerId}
                shippingMethod={shippingMethod}
                baseShippingCents={0}
                lastAddedSiteId={lastAddedSiteId}
                getItemStatus={getItemStatus}
                allAvailable={allAvailable}
                checking={checking}
                customer={customer}
                onLogin={handleLogin}
                onRegister={register}
                onLogout={handleLogout}
              />
            )}

            {step === 'shipping' && (
              <ShippingStep
                items={cart.items}
                shippingMethod={shippingMethod}
                onShippingMethodChange={setShippingMethod}
                customerContact={customerContact}
                onContactChange={handleContactChange}
                couponApplied={couponApplied}
                onBack={goBack}
                onProceed={handleProceedToPayment}
                allAvailable={allAvailable}
                checking={checking}
                shippingEval={shippingEval}
                onShippingEval={setShippingEval}
                customer={customer}
                onLogin={handleLogin}
                onRegister={register}
                onLogout={handleLogout}
              />
            )}

            {step === 'payment' && (
              <PaymentStep
                items={cart.items}
                shippingMethod={shippingMethod}
                customerContact={customerContact}
                couponApplied={couponApplied}
                shippingEval={shippingEval}
                checkoutConfig={checkoutConfig}
                checkoutState={checkoutState}
                checkoutError={checkoutError}
                onCheckout={handleCheckout}
                onBack={goBack}
              />
            )}
          </>
        )}

        <div className="card" style={{ marginTop: 24 }}>
          <h2>How It Works</h2>
          <ol className="how-it-works">
            <li>Browse items on our store websites and add them to your cart</li>
            <li>Review your cart and apply any coupon codes</li>
            <li>Choose pickup or shipping, enter your contact info</li>
            <li>Pay securely with card</li>
            <li>
              {shippingMethod === 'pickup'
                ? 'Pick up your items at the store'
                : 'Receive your items at your door'}
            </li>
          </ol>
        </div>
      </main>

      <footer className="checkout-footer">
        <p>&copy; {new Date().getFullYear()} V.I. Consignment</p>
        <p>Powered by Coast Codex</p>
      </footer>
    </div>
  );
}
